import React, { useState } from 'react';
import {
    Paper,
    Typography,
    Stack,
    TextField,
    InputAdornment,
    useTheme,
    useMediaQuery,
    Alert,
    Box,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { authService } from '../services/authService';
import { useResponsiveToast } from '../components/Toast/Toast';
import { ActionButton, CancelButton } from '../components/Button';
import styles from '../components/Login/LoginPage.module.css';
import logo from '../assets/images/logo/logo.png';

const RequestPasswordPage: React.FC = () => {
    const [email, setEmail] = useState('');
    const [username, setUsername] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { success } = useResponsiveToast();

    const handleRequestPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!email || !username) {
            setError('Please enter both email and username.');
            return;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError('Please enter a valid email address.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            await authService.requestPasswordReset({
                email,
                username,
            });
            
            success('Password reset link sent! Please check your email.');
            
            // Redirect to login page after a short delay
            setTimeout(() => {
                navigate('/login');
            }, 2000);
        } catch (err: any) {
            setError(err.message || 'Failed to send password reset link. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleBackToLogin = () => {
        navigate('/login');
    };

    return (
        <div className={styles.loginBg}>
            <Paper elevation={3} className={styles.loginCard}>
                <Stack spacing={isMobile ? 2 : 3} alignItems='center'>
                    <img
                        src={logo}
                        alt='Gensize Logo'
                        style={{
                            width: isMobile ? '120px' : '150px',
                        }}
                    />
                    <Typography variant='h5' className={styles.heading}>
                        Request New Password
                    </Typography>
                    <Typography 
                        variant='body2' 
                        sx={{ 
                            textAlign: 'center', 
                            color: '#666',
                            fontSize: isMobile ? '12px' : '14px',
                            fontFamily: 'Poppins, sans-serif',
                            mb: 1,
                        }}
                    >
                        Enter your email and username to receive a password reset link
                    </Typography>
                    <form onSubmit={handleRequestPassword} className={styles.form} autoComplete='off'>
                        <Stack spacing={isMobile ? 1.5 : 2}>
                            <TextField
                                label='Email'
                                type='email'
                                value={email}
                                onChange={e => {
                                    setEmail(e.target.value);
                                    if (error) setError('');
                                }}
                                fullWidth
                                required
                                autoFocus
                                variant='outlined'
                                size={isMobile ? 'small' : 'medium'}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '4px',
                                    },
                                }}
                                slots={{
                                    input: undefined,
                                }}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position='start'>
                                                <EmailOutlinedIcon
                                                    sx={{ fontSize: isMobile ? 20 : 24 }}
                                                />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                            <TextField
                                label='Username'
                                value={username}
                                onChange={e => {
                                    setUsername(e.target.value);
                                    if (error) setError('');
                                }}
                                fullWidth
                                required
                                variant='outlined'
                                size={isMobile ? 'small' : 'medium'}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '4px',
                                    },
                                }}
                                slots={{
                                    input: undefined,
                                }}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position='start'>
                                                <PersonOutlineIcon
                                                    sx={{ fontSize: isMobile ? 20 : 24 }}
                                                />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                            {error && (
                                <Alert 
                                    severity="error" 
                                    sx={{ 
                                        mt: 1,
                                        '& .MuiAlert-message': {
                                            fontSize: isMobile ? '12px' : '14px',
                                            fontFamily: 'Poppins, sans-serif',
                                        }
                                    }}
                                >
                                    {error}
                                </Alert>
                            )}
                            <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'center', 
                                mt: isMobile ? 2 : 3,
                                mb: 2 
                            }}>
                                <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                                    <Box sx={{ flex: 1 }}>
                                        <CancelButton
                                            onClick={handleBackToLogin}
                                            startIcon={<ArrowBackIcon />}
                                            disabled={loading}
                                            sx={{
                                                color: '#DE3B40',
                                                borderColor: '#DE3B40',
                                                borderRadius: '12px',
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                fontSize: isMobile ? 14 : 16,
                                                height: `${isMobile ? 40 : 44}px !important`,
                                                width: '100% !important',
                                                minHeight: `${isMobile ? 40 : 44}px !important`,
                                                maxHeight: `${isMobile ? 40 : 44}px !important`,
                                                px: '0 !important',
                                                py: '0 !important',
                                                '&:hover': { backgroundColor: '#FFECEC' },
                                                '&:disabled': {
                                                    color: '#ccc',
                                                    borderColor: '#ccc',
                                                    backgroundColor: '#f5f5f5',
                                                },
                                            }}
                                        >
                                            Back
                                        </CancelButton>
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <ActionButton
                                            type='submit'
                                            disabled={loading}
                                            sx={{
                                                backgroundColor: '#132F5F',
                                                borderRadius: '12px',
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                fontSize: isMobile ? 14 : 16,
                                                height: `${isMobile ? 40 : 44}px !important`,
                                                width: '100% !important',
                                                minHeight: `${isMobile ? 40 : 44}px !important`,
                                                maxHeight: `${isMobile ? 40 : 44}px !important`,
                                                px: '0 !important',
                                                py: '0 !important',
                                                '&:hover': { backgroundColor: '#0f254d' },
                                                '&:disabled': { backgroundColor: '#ccc' },
                                            }}
                                        >
                                            {loading ? 'Sending...' : 'Send Reset Link'}
                                        </ActionButton>
                                    </Box>
                                </Box>
                            </Box>
                        </Stack>
                    </form>
                </Stack>
            </Paper>
        </div>
    );
};

export default RequestPasswordPage;
